import React, { useState, useEffect } from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { 
  Users, 
  Search, 
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  RefreshCw,
  LogOut,
  GraduationCap,
  BookOpen,
  Calendar,
  X,
  ClipboardCheck,
  UserCheck,
  DollarSign,
  FileText,
  Download,
  ExternalLink,
  Paperclip,
  Files,
  User,
  Mail,
  Phone,
  MessageCircle,
  MailOpen
} from "lucide-react";

import { motion, AnimatePresence } from "framer-motion";

export default function AdminDashboard({ onLogout }) {
  const [applications, setApplications] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [tutors, setTutors] = useState([]); // New state for tutors
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [messageStatusFilter, setMessageStatusFilter] = useState("all");
  const [selectedApp, setSelectedApp] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [activeTab, setActiveTab] = useState("applications");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [viewingFiles, setViewingFiles] = useState(false);
  const [selectedTutorDetails, setSelectedTutorDetails] = useState(null); // For tutor info in booking modal

  // API URL configuration
  const API_URL = import.meta.env.VITE_API_URL || 
               (window.location.hostname === 'localhost' 
                  ? 'http://localhost:5001' 
                  : 'https://api.zenithacademics.com');

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const url = statusFilter === "all" 
        ? `https://api.zenithacademics.com/api/applications`
        : `https://api.zenithacademics.com/api/applications?status=${statusFilter}`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success) {
        setApplications(data.data);
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
      alert('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const url = statusFilter === "all" 
        ? `https://api.zenithacademics.com/api/bookings`
        : `https://api.zenithacademics.com/api/bookings?status=${statusFilter}`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success) {
        const bookingsWithParsedFiles = data.data.map(booking => {
          let parsedSubjects = [];
          if (typeof booking.subjects === 'string') {
            if (booking.subjects.includes(',')) {
              parsedSubjects = booking.subjects.split(',').map(s => s.trim());
            } else if (booking.subjects.startsWith('[') && booking.subjects.endsWith(']')) {
              try {
                parsedSubjects = JSON.parse(booking.subjects);
              } catch (e) {
                parsedSubjects = [booking.subjects];
              }
            } else {
              parsedSubjects = [booking.subjects];
            }
          } else if (Array.isArray(booking.subjects)) {
            parsedSubjects = booking.subjects;
          }
          
          let parsedTimeSlots = [];
          if (typeof booking.selected_time_slots === 'string') {
            try {
              parsedTimeSlots = JSON.parse(booking.selected_time_slots);
            } catch (e) {
              parsedTimeSlots = [booking.selected_time_slots];
            }
          } else if (Array.isArray(booking.selected_time_slots)) {
            parsedTimeSlots = booking.selected_time_slots;
          }
          
          let parsedFiles = [];
          if (typeof booking.files === 'string') {
            try {
              parsedFiles = JSON.parse(booking.files);
            } catch (e) {
              parsedFiles = [];
            }
          } else if (Array.isArray(booking.files)) {
            parsedFiles = booking.files;
          }
          
          return {
            ...booking,
            subjects: parsedSubjects,
            selected_time_slots: parsedTimeSlots,
            files: parsedFiles
          };
        });
        setBookings(bookingsWithParsedFiles);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      alert('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const fetchContactMessages = async () => {
    setLoading(true);
    try {
      const url = messageStatusFilter === "all" 
        ? `https://api.zenithacademics.com/api/contact`
        : `https://api.zenithacademics.com/api/contact?status=${messageStatusFilter}`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success) {
        setContactMessages(data.data);
      }
    } catch (error) {
      console.error('Error fetching contact messages:', error);
      alert('Failed to load contact messages');
    } finally {
      setLoading(false);
    }
  };

  // New function to fetch tutors
  const fetchTutors = async () => {
    try {
      const response = await fetch(`https://api.zenithacademics.com/api/tutors/approved`);
      const data = await response.json();
      
      if (data.success) {
        setTutors(data.data);
      }
    } catch (error) {
      console.error('Error fetching tutors:', error);
    }
  };

  useEffect(() => {
    if (activeTab === "applications") {
      fetchApplications();
    } else if (activeTab === "bookings") {
      fetchBookings();
    } else if (activeTab === "messages") {
      fetchContactMessages();
    }
    
    // Always fetch tutors data when component mounts
    fetchTutors();
  }, [statusFilter, activeTab, messageStatusFilter]);

  // Function to get tutor details by ID
  const getTutorDetails = (tutorId) => {
    return tutors.find(tutor => tutor.id === tutorId || tutor._id === tutorId);
  };

  // When a booking is selected, also fetch tutor details
  const handleSelectBooking = async (booking) => {
    setSelectedBooking(booking);
    
    // Try to find tutor details
    if (booking.tutor_id) {
      const tutorDetails = getTutorDetails(booking.tutor_id);
      setSelectedTutorDetails(tutorDetails || null);
    }
  };

  const updateApplicationStatus = async (id, newStatus) => {
    try {
      const response = await fetch(`https://api.zenithacademics.com/api/applications/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      const data = await response.json();
      
      if (data.success) {
        alert(`Application ${newStatus}!`);
        fetchApplications();
        setSelectedApp(null);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const updateBookingStatus = async (id, newStatus) => {
    try {
      const response = await fetch(`https://api.zenithacademics.com/api/bookings/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      const data = await response.json();
      
      if (data.success) {
        alert(`Booking ${newStatus}!`);
        fetchBookings();
        setSelectedBooking(null);
        setSelectedTutorDetails(null);
      }
    } catch (error) {
      console.error('Error updating booking status:', error);
      alert('Failed to update booking status');
    }
  };

  const updateMessageStatus = async (id, newStatus) => {
    try {
      const response = await fetch(`https://api.zenithacademics.com/api/contact/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      const data = await response.json();
      
      if (data.success) {
        alert(`Message marked as ${newStatus}!`);
        fetchContactMessages();
        setSelectedMessage(null);
      }
    } catch (error) {
      console.error('Error updating message status:', error);
      alert('Failed to update message status');
    }
  };

  const deleteApplication = async (id) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;

    try {
      const response = await fetch(`https://api.zenithacademics.com/api/applications/${id}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      
      if (data.success) {
        alert('Application deleted!');
        fetchApplications();
        setSelectedApp(null);
      }
    } catch (error) {
      console.error('Error deleting application:', error);
      alert('Failed to delete application');
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;

    try {
      const response = await fetch(`https://api.zenithacademics.com/api/bookings/${id}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      
      if (data.success) {
        alert('Booking deleted!');
        fetchBookings();
        setSelectedBooking(null);
        setSelectedTutorDetails(null);
      }
    } catch (error) {
      console.error('Error deleting booking:', error);
      alert('Failed to delete booking');
    }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;

    try {
      const response = await fetch(`https://api.zenithacademics.com/api/contact/${id}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      
      if (data.success) {
        alert('Message deleted!');
        fetchContactMessages();
        setSelectedMessage(null);
      }
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Failed to delete message');
    }
  };

  const handleViewFiles = (booking) => {
    setSelectedFiles(booking.files || []);
    setViewingFiles(true);
  };

  const handleDownloadFile = async (file) => {
    try {
      if (!file.url) {
        throw new Error('File URL not available');
      }

      let fileUrl = file.url;
      if (!fileUrl.startsWith('http')) {
        fileUrl = `https://api.zenithacademics.com/${fileUrl}`;
      }

      console.log('Downloading file from:', fileUrl);

      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name || 'study-material';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert(`Failed to download file: ${error.message}. Please make sure the backend server is running on port 5001.`);
    }
  };

  const handleViewFile = (file) => {
    try {
      if (!file.url) {
        throw new Error('File URL not available');
      }

      let fileUrl = file.url;
      if (!fileUrl.startsWith('http')) {
        fileUrl = `https://api.zenithacademics.com/${fileUrl}`;
      }

      console.log('Opening file:', fileUrl);
      window.open(fileUrl, '_blank');
    } catch (error) {
      console.error('Error viewing file:', error);
      alert(`Failed to view file: ${error.message}. Please make sure the backend server is running on port 5001.`);
    }
  };

  const handleDownloadResume = async (application) => {
    try {
      if (!application.resume_url) {
        alert('No resume available for this application');
        return;
      }

      let fileUrl = application.resume_url;
      if (!fileUrl.startsWith('http')) {
        fileUrl = `https://api.zenithacademics.com/${fileUrl}`;
      }

      console.log('Downloading resume from:', fileUrl);

      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${application.full_name.replace(/\s+/g, '_')}_Resume.${getFileExtension(blob.type)}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading resume:', error);
      alert(`Failed to download resume: ${error.message}. Please make sure the backend server is running on port 5001.`);
    }
  };

  const handleViewResume = (application) => {
    try {
      if (!application.resume_url) {
        alert('No resume available for this application');
        return;
      }

      let fileUrl = application.resume_url;
      if (!fileUrl.startsWith('http')) {
        fileUrl = `https://api.zenithacademics.com/${fileUrl}`;
      }

      console.log('Opening resume:', fileUrl);
      window.open(fileUrl, '_blank');
    } catch (error) {
      console.error('Error viewing resume:', error);
      alert(`Failed to view resume: ${error.message}. Please make sure the backend server is running on port 5001.`);
    }
  };

  const getFileExtension = (mimeType) => {
    const extensions = {
      'application/pdf': 'pdf',
      'application/msword': 'doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx'
    };
    return extensions[mimeType] || 'file';
  };

  const handleReplyToMessage = (message) => {
    const subject = encodeURIComponent(`Re: Your message to Zenith Academics`);
    const body = encodeURIComponent(`Dear ${message.name},\n\nThank you for reaching out to Zenith Academics. ` +
      `Regarding your message: "${message.message.substring(0, 100)}..."\n\n` +
      `Best regards,\nZenith Academics Team`);
    
    window.open(`mailto:${message.email}?subject=${subject}&body=${body}`);
  };

  const handleLogout = () => {
    sessionStorage.removeItem("adminLoggedIn");
    sessionStorage.removeItem("adminUsername");
    onLogout();
  };

  const filteredData = (() => {
    if (activeTab === "applications") {
      return applications.filter(item =>
        item.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.current_school?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    } else if (activeTab === "bookings") {
      return bookings.filter(item =>
        item.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.student_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.parent_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.parent_phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.tutor_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    } else if (activeTab === "messages") {
      return contactMessages.filter(item =>
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.message?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return [];
  })();

  const ApplicationStatusBadge = ({ status }) => {
    if (!status) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300">
          <Clock className="w-3 h-3" />
          Unknown
        </span>
      );
    }

    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      reviewing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      approved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
    };

    const icons = {
      pending: Clock,
      reviewing: Eye,
      approved: CheckCircle,
      rejected: XCircle
    };

    const Icon = icons[status] || Clock;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${colors[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'}`}>
        <Icon className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const BookingStatusBadge = ({ status }) => {
    if (!status) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300">
          <Clock className="w-3 h-3" />
          Unknown
        </span>
      );
    }

    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      confirmed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      completed: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
    };

    const icons = {
      pending: Clock,
      confirmed: CheckCircle,
      completed: ClipboardCheck,
      cancelled: XCircle
    };

    const Icon = icons[status] || Clock;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${colors[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'}`}>
        <Icon className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const MessageStatusBadge = ({ status }) => {
    const displayStatus = status === 'unread' ? 'new' : status;
    
    const colors = {
      new: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      read: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      replied: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      archived: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
    };

    const icons = {
      new: Mail,
      read: MailOpen,
      replied: CheckCircle,
      archived: XCircle
    };

    const Icon = icons[displayStatus] || Mail;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${colors[displayStatus] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'}`}>
        <Icon className="w-3 h-3" />
        {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
      </span>
    );
  };

  const getFileTypeColor = (fileType) => {
    if (!fileType) return 'bg-gray-500';
    
    if (fileType.includes('pdf')) return 'bg-red-500';
    if (fileType.includes('word') || fileType.includes('doc')) return 'bg-blue-500';
    if (fileType.includes('image') || fileType.includes('jpg') || fileType.includes('png')) return 'bg-green-500';
    if (fileType.includes('text')) return 'bg-yellow-500';
    
    return 'bg-gray-500';
  };

  const applicationStats = {
    total: applications.length,
    pending: applications.filter(a => a.application_status === 'pending').length,
    approved: applications.filter(a => a.application_status === 'approved').length,
    rejected: applications.filter(a => a.application_status === 'rejected').length
  };

  const bookingStats = {
    total: bookings.length,
    pending: bookings.filter(b => b.status === 'pending').length,
    confirmed: bookings.filter(b => b.status === 'confirmed').length,
    completed: bookings.filter(b => b.status === 'completed').length,
    cancelled: bookings.filter(b => b.status === 'cancelled').length
  };

  const messageStats = {
    total: contactMessages.length,
    new: contactMessages.filter(m => m.status === 'unread' || m.status === 'new' || !m.status).length,
    read: contactMessages.filter(m => m.status === 'read').length,
    replied: contactMessages.filter(m => m.status === 'replied').length,
    archived: contactMessages.filter(m => m.status === 'archived').length
  };

  const currentStats = activeTab === "applications" ? applicationStats : 
                     activeTab === "bookings" ? bookingStats : 
                     messageStats;

  const refreshData = () => {
    if (activeTab === "applications") fetchApplications();
    else if (activeTab === "bookings") fetchBookings();
    else if (activeTab === "messages") fetchContactMessages();
  };

  const parseApplicationSubjects = (application) => {
    if (!application || !application.subjects) return [];
    
    let subjectsArray = [];
    if (Array.isArray(application.subjects)) {
      subjectsArray = application.subjects;
    } else if (typeof application.subjects === 'string') {
      if (application.subjects.includes(',')) {
        subjectsArray = application.subjects.split(',').map(s => s.trim());
      } else if (application.subjects.startsWith('[') && application.subjects.endsWith(']')) {
        try {
          subjectsArray = JSON.parse(application.subjects);
        } catch (e) {
          subjectsArray = [application.subjects];
        }
      } else {
        subjectsArray = [application.subjects];
      }
    }
    
    return subjectsArray;
  };

  // Helper function to format availability for display
  const formatAvailabilityForDisplay = (availability) => {
    if (!availability) return "No availability set";
    
    if (typeof availability === 'string') {
      try {
        availability = JSON.parse(availability);
      } catch (e) {
        return "Availability format error";
      }
    }
    
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    let result = [];
    
    days.forEach(day => {
      if (availability[day]) {
        let times = availability[day];
        if (Array.isArray(times) && times.length > 0) {
          result.push(`${day}: ${times.join(', ')}`);
        } else if (typeof times === 'string') {
          result.push(`${day}: ${times}`);
        }
      }
    });
    
    return result.length > 0 ? result.join(' • ') : "No specific availability";
  };

  // Helper function to get day availability
  const getDayAvailability = (availability, dayName) => {
    if (!availability) return [];
    
    if (typeof availability === 'string') {
      try {
        availability = JSON.parse(availability);
      } catch (e) {
        return [];
      }
    }
    
    const dayAvailability = availability[dayName];
    if (!dayAvailability) return [];
    
    if (Array.isArray(dayAvailability)) {
      return dayAvailability;
    } else if (typeof dayAvailability === 'string') {
      return [dayAvailability];
    } else if (dayAvailability.timeSlots && Array.isArray(dayAvailability.timeSlots)) {
      return dayAvailability.timeSlots;
    }
    
    return [];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50 dark:bg-gradient-to-br dark:from-zinc-950 dark:via-purple-950/20 dark:to-zinc-950 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl md:text-5xl font-black bg-gradient-to-r from-purple-600 to-pink-600 dark:from-purple-400 dark:to-pink-400 bg-clip-text text-transparent">
              Admin Dashboard
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">Manage tutor applications, bookings, and contact messages</p>
          </div>
          <Button
            onClick={handleLogout}
            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </Button>
        </div>

        {/* Navigation Tabs */}
        <div className="flex mb-8 border-b-2 border-gray-200 dark:border-zinc-700 overflow-x-auto">
          <button
            onClick={() => {
              setActiveTab("applications");
              setSearchTerm("");
              setStatusFilter("all");
            }}
            className={`flex items-center gap-2 px-6 py-3 font-bold text-lg transition-all whitespace-nowrap ${
              activeTab === "applications"
                ? "text-purple-600 dark:text-purple-400 border-b-2 border-purple-600 dark:border-purple-400"
                : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            }`}
          >
            <Users className="w-5 h-5" />
            Tutor Applications
          </button>
          <button
            onClick={() => {
              setActiveTab("bookings");
              setSearchTerm("");
              setStatusFilter("all");
            }}
            className={`flex items-center gap-2 px-6 py-3 font-bold text-lg transition-all whitespace-nowrap ${
              activeTab === "bookings"
                ? "text-purple-600 dark:text-purple-400 border-b-2 border-purple-600 dark:border-purple-400"
                : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            }`}
          >
            <Calendar className="w-5 h-5" />
            Bookings
          </button>
          <button
            onClick={() => {
              setActiveTab("messages");
              setSearchTerm("");
              setMessageStatusFilter("all");
            }}
            className={`flex items-center gap-2 px-6 py-3 font-bold text-lg transition-all whitespace-nowrap ${
              activeTab === "messages"
                ? "text-purple-600 dark:text-purple-400 border-b-2 border-purple-600 dark:border-purple-400"
                : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            }`}
          >
            <MessageCircle className="w-5 h-5" />
            Contact Messages
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {activeTab === "applications" ? (
            <>
              <Card className="p-4 bg-white dark:bg-zinc-900 border-2 border-purple-200 dark:border-purple-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total</p>
                    <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">{applicationStats.total}</p>
                  </div>
                  <Users className="w-8 h-8 text-purple-400" />
                </div>
              </Card>
              <Card className="p-4 bg-white dark:bg-zinc-900 border-2 border-yellow-200 dark:border-yellow-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Pending</p>
                    <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">{applicationStats.pending}</p>
                  </div>
                  <Clock className="w-8 h-8 text-yellow-400" />
                </div>
              </Card>
              <Card className="p-4 bg-white dark:bg-zinc-900 border-2 border-green-200 dark:border-green-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Approved</p>
                    <p className="text-3xl font-bold text-green-600 dark:text-green-400">{applicationStats.approved}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
              </Card>
              <Card className="p-4 bg-white dark:bg-zinc-900 border-2 border-red-200 dark:border-red-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Rejected</p>
                    <p className="text-3xl font-bold text-red-600 dark:text-red-400">{applicationStats.rejected}</p>
                  </div>
                  <XCircle className="w-8 h-8 text-red-400" />
                </div>
              </Card>
            </>
          ) : activeTab === "bookings" ? (
            <>
              <Card className="p-4 bg-white dark:bg-zinc-900 border-2 border-purple-200 dark:border-purple-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total</p>
                    <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">{bookingStats.total}</p>
                  </div>
                  <Calendar className="w-8 h-8 text-purple-400" />
                </div>
              </Card>
              <Card className="p-4 bg-white dark:bg-zinc-900 border-2 border-yellow-200 dark:border-yellow-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Pending</p>
                    <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">{bookingStats.pending}</p>
                  </div>
                  <Clock className="w-8 h-8 text-yellow-400" />
                </div>
              </Card>
              <Card className="p-4 bg-white dark:bg-zinc-900 border-2 border-green-200 dark:border-green-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Confirmed</p>
                    <p className="text-3xl font-bold text-green-600 dark:text-green-400">{bookingStats.confirmed}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
              </Card>
              <Card className="p-4 bg-white dark:bg-zinc-900 border-2 border-blue-200 dark:border-blue-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Completed</p>
                    <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{bookingStats.completed}</p>
                  </div>
                  <ClipboardCheck className="w-8 h-8 text-blue-400" />
                </div>
              </Card>
            </>
          ) : (
            <>
              <Card className="p-4 bg-white dark:bg-zinc-900 border-2 border-purple-200 dark:border-purple-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total</p>
                    <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">{messageStats.total}</p>
                  </div>
                  <MessageCircle className="w-8 h-8 text-purple-400" />
                </div>
              </Card>
              <Card className="p-4 bg-white dark:bg-zinc-900 border-2 border-yellow-200 dark:border-yellow-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">New</p>
                    <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">{messageStats.new}</p>
                  </div>
                  <Mail className="w-8 h-8 text-yellow-400" />
                </div>
              </Card>
              <Card className="p-4 bg-white dark:bg-zinc-900 border-2 border-blue-200 dark:border-blue-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Read</p>
                    <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{messageStats.read}</p>
                  </div>
                  <MailOpen className="w-8 h-8 text-blue-400" />
                </div>
              </Card>
              <Card className="p-4 bg-white dark:bg-zinc-900 border-2 border-green-200 dark:border-green-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Replied</p>
                    <p className="text-3xl font-bold text-green-600 dark:text-green-400">{messageStats.replied}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
              </Card>
            </>
          )}
        </div>

        {/* Filters */}
        <Card className="p-6 mb-6 bg-white dark:bg-zinc-900">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder={activeTab === "applications" 
                  ? "Search by name, email, or school..." 
                  : activeTab === "bookings" 
                  ? "Search by student name, email, parent contact, or tutor..."
                  : "Search by name, email, or message content..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 dark:border-zinc-700 rounded-xl focus:border-purple-500 dark:focus:border-purple-500 bg-white dark:bg-zinc-800 text-gray-900 dark:text-white"
              />
            </div>

            {/* Status Filter */}
            <div className="flex gap-2 flex-wrap">
              {activeTab === "applications" ? (
                ['all', 'pending', 'reviewing', 'approved', 'rejected'].map(status => (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      statusFilter === status
                        ? 'bg-purple-600 text-white hover:bg-purple-700'
                        : 'bg-gray-200 dark:bg-zinc-800 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-zinc-700'
                    }`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))
              ) : activeTab === "bookings" ? (
                ['all', 'pending', 'confirmed', 'completed', 'cancelled'].map(status => (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      statusFilter === status
                        ? 'bg-purple-600 text-white hover:bg-purple-700'
                        : 'bg-gray-200 dark:bg-zinc-800 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-zinc-700'
                    }`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))
              ) : (
                ['all', 'new', 'read', 'replied', 'archived'].map(status => (
                  <button
                    key={status}
                    onClick={() => setMessageStatusFilter(status)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      messageStatusFilter === status
                        ? 'bg-purple-600 text-white hover:bg-purple-700'
                        : 'bg-gray-200 dark:bg-zinc-800 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-zinc-700'
                    }`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))
              )}
              <button 
                onClick={refreshData} 
                className="px-4 py-2 rounded-lg font-medium bg-blue-500 text-white hover:bg-blue-600 transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>
        </Card>

        {/* Data Table */}
        <Card className="p-6 bg-white dark:bg-zinc-900">
          {loading ? (
            <div className="text-center py-12">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="inline-block"
              >
                <RefreshCw className="w-8 h-8 text-purple-500" />
              </motion.div>
              <p className="mt-4 text-gray-600 dark:text-gray-400">Loading {activeTab}...</p>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-12">
              {activeTab === "applications" ? (
                <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              ) : activeTab === "bookings" ? (
                <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              ) : (
                <MessageCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              )}
              <p className="text-gray-600 dark:text-gray-400">No {activeTab} found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200 dark:border-zinc-700">
                    {activeTab === "applications" ? (
                      <>
                        <th className="text-left p-4 font-bold text-gray-700 dark:text-gray-300">Name</th>
                        <th className="text-left p-4 font-bold text-gray-700 dark:text-gray-300">Email</th>
                        <th className="text-left p-4 font-bold text-gray-700 dark:text-gray-300">School</th>
                        <th className="text-left p-4 font-bold text-gray-700 dark:text-gray-300">Resume</th>
                        <th className="text-left p-4 font-bold text-gray-700 dark:text-gray-300">Status</th>
                        <th className="text-left p-4 font-bold text-gray-700 dark:text-gray-300">Date</th>
                        <th className="text-left p-4 font-bold text-gray-700 dark:text-gray-300">Actions</th>
                      </>
                    ) : activeTab === "bookings" ? (
                      <>
                        <th className="text-left p-4 font-bold text-gray-700 dark:text-gray-300">Student</th>
                        <th className="text-left p-4 font-bold text-gray-700 dark:text-gray-300">Parent Email</th>
                        <th className="text-left p-4 font-bold text-gray-700 dark:text-gray-300">Parent Phone</th>
                        <th className="text-left p-4 font-bold text-gray-700 dark:text-gray-300">Tutor</th>
                        <th className="text-left p-4 font-bold text-gray-700 dark:text-gray-300">Files</th>
                        <th className="text-left p-4 font-bold text-gray-700 dark:text-gray-300">Status</th>
                        <th className="text-left p-4 font-bold text-gray-700 dark:text-gray-300">Date</th>
                        <th className="text-left p-4 font-bold text-gray-700 dark:text-gray-300">Actions</th>
                      </>
                    ) : (
                      <>
                        <th className="text-left p-4 font-bold text-gray-700 dark:text-gray-300">Name</th>
                        <th className="text-left p-4 font-bold text-gray-700 dark:text-gray-300">Email</th>
                        <th className="text-left p-4 font-bold text-gray-700 dark:text-gray-300">Message Preview</th>
                        <th className="text-left p-4 font-bold text-gray-700 dark:text-gray-300">Status</th>
                        <th className="text-left p-4 font-bold text-gray-700 dark:text-gray-300">Date</th>
                        <th className="text-left p-4 font-bold text-gray-700 dark:text-gray-300">Actions</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((item) => (
                    <motion.tr
                      key={item.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="border-b border-gray-100 dark:border-zinc-800 hover:bg-purple-50 dark:hover:bg-zinc-800/50 transition-colors"
                    >
                      {activeTab === "applications" ? (
                        <>
                          <td className="p-4 font-medium text-gray-900 dark:text-white">{item.full_name}</td>
                          <td className="p-4 text-gray-600 dark:text-gray-400">{item.email}</td>
                          <td className="p-4 text-gray-600 dark:text-gray-400">{item.current_school}</td>
                          <td className="p-4">
                            {item.resume_url ? (
                              <button
                                onClick={() => handleViewResume(item)}
                                className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm flex items-center gap-1 transition-colors"
                              >
                                <Download className="w-3 h-3" />
                                View Resume
                              </button>
                            ) : (
                              <span className="text-xs text-gray-500 dark:text-gray-400">No resume</span>
                            )}
                          </td>
                          <td className="p-4">
                            <ApplicationStatusBadge status={item.application_status} />
                          </td>
                          <td className="p-4 text-gray-600 dark:text-gray-400">
                            {new Date(item.created_at).toLocaleDateString()}
                          </td>
                          <td className="p-4">
                            <button
                              onClick={() => setSelectedApp(item)}
                              className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-medium flex items-center gap-1 transition-colors"
                            >
                              <Eye className="w-4 h-4" />
                              View
                            </button>
                          </td>
                        </>
                      ) : activeTab === "bookings" ? (
                        <>
                          <td className="p-4 font-medium text-gray-900 dark:text-white">{item.student_name}</td>
                          <td className="p-4 text-gray-600 dark:text-gray-400">
                            <div className="flex items-center gap-1">
                              <Mail className="w-3 h-3 text-gray-500" />
                              <span className="truncate max-w-[150px]">{item.parent_email || "N/A"}</span>
                            </div>
                          </td>
                          <td className="p-4 text-gray-600 dark:text-gray-400">
                            <div className="flex items-center gap-1">
                              <Phone className="w-3 h-3 text-gray-500" />
                              <span>{item.parent_phone || "N/A"}</span>
                            </div>
                          </td>
                          <td className="p-4 text-gray-600 dark:text-gray-400">{item.tutor_name}</td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <Paperclip className="w-4 h-4 text-gray-500" />
                              <span className="text-sm font-medium">
                                {item.files?.length || 0} file{item.files?.length !== 1 ? 's' : ''}
                              </span>
                              {item.files?.length > 0 && (
                                <button
                                  onClick={() => handleViewFiles(item)}
                                  className="px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs flex items-center gap-1 transition-colors"
                                >
                                  <Eye className="w-3 h-3" />
                                  View
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="p-4">
                            <BookingStatusBadge status={item.status} />
                          </td>
                          <td className="p-4 text-gray-600 dark:text-gray-400">
                            {new Date(item.created_at).toLocaleDateString()}
                          </td>
                          <td className="p-4">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleSelectBooking(item)}
                                className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-medium flex items-center gap-1 transition-colors"
                              >
                                <Eye className="w-4 h-4" />
                                Details
                              </button>
                              {item.files?.length > 0 && (
                                <button
                                  onClick={() => handleViewFiles(item)}
                                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium flex items-center gap-1 transition-colors"
                                >
                                  <Files className="w-4 h-4" />
                                  Files
                                </button>
                              )}
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="p-4 font-medium text-gray-900 dark:text-white">{item.name}</td>
                          <td className="p-4 text-gray-600 dark:text-gray-400">{item.email}</td>
                          <td className="p-4 text-gray-600 dark:text-gray-400">
                            <div className="truncate max-w-[300px]">
                              {item.message?.substring(0, 100)}...
                            </div>
                          </td>
                          <td className="p-4">
                            <MessageStatusBadge status={item.status} />
                          </td>
                          <td className="p-4 text-gray-600 dark:text-gray-400">
                            {new Date(item.created_at).toLocaleDateString()}
                          </td>
                          <td className="p-4">
                            <button
                              onClick={() => setSelectedMessage(item)}
                              className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-medium flex items-center gap-1 transition-colors"
                            >
                              <Eye className="w-4 h-4" />
                              View
                            </button>
                          </td>
                        </>
                      )}
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* Files View Modal */}
      <AnimatePresence>
        {viewingFiles && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setViewingFiles(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-zinc-900 rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Uploaded Files</h2>
                  <p className="text-gray-600 dark:text-gray-400 mt-1">
                    {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} uploaded by student
                  </p>
                </div>
                <button
                  onClick={() => setViewingFiles(false)}
                  className="w-10 h-10 rounded-full bg-gray-200 dark:bg-zinc-800 flex items-center justify-center hover:bg-gray-300 dark:hover:bg-zinc-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Files List */}
              <div className="space-y-4">
                {selectedFiles.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">No files uploaded</p>
                  </div>
                ) : (
                  selectedFiles.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 border-2 border-gray-200 dark:border-zinc-700 rounded-xl hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <div className={`p-3 rounded-lg ${getFileTypeColor(file.type)}`}>
                          <FileText className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900 dark:text-white truncate">{file.name}</h4>
                          <div className="flex items-center gap-4 mt-1">
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {file.type || 'Unknown type'}
                            </span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {(file.size / 1024).toFixed(1)} KB
                            </span>
                          </div>
                          {file.url && (
                            <div className="mt-2">
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                URL: {file.url}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleDownloadFile(file)}
                          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium flex items-center gap-1 transition-colors"
                        >
                          <Download className="w-4 h-4" />
                          Download
                        </button>
                        <button
                          onClick={() => handleViewFile(file)}
                          className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium flex items-center gap-1 transition-colors"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Open
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Footer */}
              <div className="mt-8 pt-6 border-t border-gray-200 dark:border-zinc-700">
                <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                  These files were submitted by the student as study materials for the tutoring sessions
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Application Detail Modal */}
      <AnimatePresence>
        {selectedApp && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedApp(null)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-zinc-900 rounded-2xl p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Application Details</h2>
                <button
                  onClick={() => setSelectedApp(null)}
                  className="w-10 h-10 rounded-full bg-gray-200 dark:bg-zinc-800 flex items-center justify-center hover:bg-gray-300 dark:hover:bg-zinc-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="space-y-6">
                {/* Personal Information */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <User className="w-5 h-5 text-purple-500" />
                    Personal Information
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Full Name</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{selectedApp.full_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Email</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{selectedApp.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Phone</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{selectedApp.phone}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Status</p>
                      <div className="mt-1">
                        <ApplicationStatusBadge status={selectedApp.application_status} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Resume Section */}
                {selectedApp.resume_url && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-purple-500" />
                      Resume
                    </h3>
                    <div className="p-4 border-2 border-purple-200 dark:border-purple-800 rounded-xl bg-purple-50 dark:bg-purple-900/20">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-purple-500 rounded-lg">
                            <FileText className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-white">Applicant's Resume</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Click to view or download the resume
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleViewResume(selectedApp)}
                            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium flex items-center gap-2 transition-colors"
                          >
                            <ExternalLink className="w-4 h-4" />
                            View
                          </button>
                          <button
                            onClick={() => handleDownloadResume(selectedApp)}
                            className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium flex items-center gap-2 transition-colors"
                          >
                            <Download className="w-4 h-4" />
                            Download
                          </button>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-purple-200 dark:border-purple-700">
                        <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                          URL: {selectedApp.resume_url}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Education */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-purple-500" />
                    Education
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Current School</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{selectedApp.current_school}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Graduation Year</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{selectedApp.graduation_year}</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Education Background</p>
                    <p className="font-semibold text-gray-900 dark:text-white mt-1">{selectedApp.education}</p>
                  </div>
                </div>

                {/* Subjects */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-purple-500" />
                    Subjects
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {parseApplicationSubjects(selectedApp).map((subject, idx) => (
                      <span key={idx} className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-sm font-semibold">
                        {subject}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Experience */}
                {selectedApp.experience && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Experience</h3>
                    <p className="text-gray-700 dark:text-gray-300">{selectedApp.experience}</p>
                  </div>
                )}

                {/* Availability */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-purple-500" />
                    Availability
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300">{selectedApp.availability}</p>
                </div>

                {/* Motivation */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Motivation</h3>
                  <p className="text-gray-700 dark:text-gray-300">{selectedApp.motivation}</p>
                </div>

                {/* Achievements */}
                {selectedApp.achievements && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Achievements</h3>
                    <p className="text-gray-700 dark:text-gray-300">{selectedApp.achievements}</p>
                  </div>
                )}

                {/* Date Submitted */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Application Timeline</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Submitted</p>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {new Date(selectedApp.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Last Updated</p>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {new Date(selectedApp.updated_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-6 border-t border-gray-200 dark:border-zinc-700">
                  <button
                    onClick={() => updateApplicationStatus(selectedApp.id, 'approved')}
                    className="flex-1 px-4 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={selectedApp.application_status === 'approved'}
                  >
                    <CheckCircle className="w-4 h-4" />
                    Approve
                  </button>
                  <button
                    onClick={() => updateApplicationStatus(selectedApp.id, 'reviewing')}
                    className="flex-1 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={selectedApp.application_status === 'reviewing'}
                  >
                    <Eye className="w-4 h-4" />
                    Mark Reviewing
                  </button>
                  <button
                    onClick={() => updateApplicationStatus(selectedApp.id, 'rejected')}
                    className="flex-1 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={selectedApp.application_status === 'rejected'}
                  >
                    <XCircle className="w-4 h-4" />
                    Reject
                  </button>
                  <button
                    onClick={() => deleteApplication(selectedApp.id)}
                    className="px-4 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-medium flex items-center justify-center transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Booking Detail Modal - UPDATED WITH TUTOR AVAILABILITY */}
{/* Booking Detail Modal - UPDATED WITH SELECTED TIME SLOTS AND TUTOR DETAILS */}
<AnimatePresence>
  {selectedBooking && (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={() => {
        setSelectedBooking(null);
        setSelectedTutorDetails(null);
      }}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-zinc-900 rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Booking Details</h2>
          <button
            onClick={() => {
              setSelectedBooking(null);
              setSelectedTutorDetails(null);
            }}
            className="w-10 h-10 rounded-full bg-gray-200 dark:bg-zinc-800 flex items-center justify-center hover:bg-gray-300 dark:hover:bg-zinc-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-8">
          {/* Parent Information */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-500" />
              Parent/Guardian Information
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Parent Name</p>
                <p className="font-semibold text-gray-900 dark:text-white">{selectedBooking.parent_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Parent Email</p>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <p className="font-semibold text-gray-900 dark:text-white">{selectedBooking.parent_email}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Parent Phone</p>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-500" />
                  <p className="font-semibold text-gray-900 dark:text-white">{selectedBooking.parent_phone}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Student Information */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-500" />
              Student Information
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Name</p>
                <p className="font-semibold text-gray-900 dark:text-white">{selectedBooking.student_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Email</p>
                <p className="font-semibold text-gray-900 dark:text-white">{selectedBooking.student_email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Phone</p>
                <p className="font-semibold text-gray-900 dark:text-white">{selectedBooking.student_phone}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Grade Level</p>
                <p className="font-semibold text-gray-900 dark:text-white">{selectedBooking.grade_level}</p>
              </div>
            </div>
          </div>

          {/* Tutor Information - UPDATED TO SHOW BOOKED SESSIONS */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-purple-500" />
              Tutor Sessions
            </h3>
            
            <div className={`p-4 border-2 border-purple-200 dark:border-purple-800 rounded-xl bg-purple-50 dark:bg-purple-900/20 mb-4`}>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Primary Tutor</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{selectedBooking.tutor_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Tutor ID</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{selectedBooking.tutor_id}</p>
                </div>
              </div>
              
              {/* Display Booked Sessions by Tutor */}
              <div className="mt-4">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-purple-500" />
                  Booked Sessions
                </h4>
                
                {(() => {
                  // Parse the selected_time_slots to display properly
                  let bookedSessions = [];
                  
                  if (selectedBooking.selected_time_slots) {
                    try {
                      let slots = selectedBooking.selected_time_slots;
                      
                      // If it's a string, try to parse it
                      if (typeof slots === 'string') {
                        try {
                          slots = JSON.parse(slots);
                        } catch (e) {
                          // If it's a simple string with comma separation
                          if (slots.includes(',')) {
                            slots = slots.split(',').map(s => ({ time: s.trim() }));
                          } else {
                            slots = [{ time: slots }];
                          }
                        }
                      }
                      
                      // Ensure it's an array
                      if (!Array.isArray(slots)) {
                        slots = [slots];
                      }
                      
                      // Group sessions by tutor and subject
                      const sessionsByTutor = {};
                      
                      slots.forEach((slot, index) => {
                        const tutorName = slot.tutorName || slot.tutor_name || selectedBooking.tutor_name || "Unknown Tutor";
                        const subject = slot.subject || selectedBooking.subjects?.[0] || "General";
                        const date = slot.date || "Not specified";
                        const time = slot.time || slot;
                        
                        if (!sessionsByTutor[tutorName]) {
                          sessionsByTutor[tutorName] = {
                            tutorName,
                            sessions: []
                          };
                        }
                        
                        sessionsByTutor[tutorName].sessions.push({
                          id: index,
                          subject,
                          date,
                          time: typeof time === 'string' ? time : JSON.stringify(time),
                          originalSlot: slot
                        });
                      });
                      
                      // Convert to array
                      bookedSessions = Object.values(sessionsByTutor);
                      
                    } catch (error) {
                      console.error("Error parsing booked sessions:", error);
                    }
                  }
                  
                  if (bookedSessions.length === 0) {
                    return (
                      <div className="text-center py-4">
                        <p className="text-gray-600 dark:text-gray-400">No specific session details available</p>
                      </div>
                    );
                  }
                  
                  return (
                    <div className="space-y-4">
                      {bookedSessions.map((tutorSession, tutorIndex) => (
                        <div key={tutorIndex} className="border border-gray-200 dark:border-zinc-700 rounded-lg p-4 bg-white dark:bg-zinc-800">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <User className="w-5 h-5 text-blue-500" />
                              <div>
                                <p className="font-semibold text-gray-900 dark:text-white">{tutorSession.tutorName}</p>
                                <p className="text-xs text-gray-600 dark:text-gray-400">
                                  {tutorSession.sessions.length} session{tutorSession.sessions.length !== 1 ? 's' : ''}
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            {tutorSession.sessions.map((session, sessionIndex) => (
                              <div 
                                key={sessionIndex} 
                                className="flex items-center justify-between p-3 border border-gray-100 dark:border-zinc-700 rounded-lg bg-gray-50 dark:bg-zinc-900/50"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                                    <BookOpen className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-gray-900 dark:text-white">
                                      {session.subject}
                                    </p>
                                    <div className="flex items-center gap-3 mt-1">
                                      {session.date && session.date !== "Not specified" && (
                                        <div className="flex items-center gap-1">
                                          <Calendar className="w-3 h-3 text-gray-500" />
                                          <span className="text-xs text-gray-600 dark:text-gray-400">
                                            {session.date}
                                          </span>
                                        </div>
                                      )}
                                      <div className="flex items-center gap-1">
                                        <Clock className="w-3 h-3 text-gray-500" />
                                        <span className="text-xs text-gray-600 dark:text-gray-400">
                                          {typeof session.time === 'string' && session.time.includes('T') 
                                            ? new Date(session.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                            : session.time
                                          }
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-2">
                                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                    session.subject?.toLowerCase().includes('math') 
                                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                                      : session.subject?.toLowerCase().includes('english') 
                                      ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                      : session.subject?.toLowerCase().includes('science') 
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                      : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                                  }`}>
                                    {session.subject}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {/* Summary for this tutor */}
                          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-zinc-700">
                            <div className="flex justify-between items-center">
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-gray-500" />
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                  Total with {tutorSession.tutorName}: {tutorSession.sessions.length} session{tutorSession.sessions.length !== 1 ? 's' : ''}
                                </span>
                              </div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {(() => {
                                  const uniqueSubjects = [...new Set(tutorSession.sessions.map(s => s.subject))];
                                  return uniqueSubjects.join(', ');
                                })()}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {/* Summary of all booked sessions */}
                      <div className="mt-4 p-4 border border-green-200 dark:border-green-800 rounded-lg bg-green-50 dark:bg-green-900/20">
                        <div className="flex items-center justify-between">
                          <div>
                            <h5 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                              <ClipboardCheck className="w-4 h-4 text-green-600 dark:text-green-400" />
                              Booking Summary
                            </h5>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              {bookedSessions.length} tutor{bookedSessions.length !== 1 ? 's' : ''}, {
                                bookedSessions.reduce((total, tutor) => total + tutor.sessions.length, 0)
                              } total sessions
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              Subjects: {(() => {
                                const allSubjects = bookedSessions.flatMap(tutor => 
                                  tutor.sessions.map(s => s.subject)
                                );
                                const uniqueSubjects = [...new Set(allSubjects)];
                                return uniqueSubjects.join(', ');
                              })()}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Subjects Summary */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-purple-500" />
              Subjects Summary
            </h3>
            <div className="flex flex-wrap gap-2">
              {selectedBooking.subjects?.map((subject, idx) => (
                <span key={idx} className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-sm font-semibold">
                  {subject}
                </span>
              ))}
            </div>
          </div>

          {/* Files Section */}
          {(selectedBooking.files?.length > 0) && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-500" />
                Uploaded Study Materials ({selectedBooking.files.length})
              </h3>
              <div className="space-y-2">
                {selectedBooking.files.slice(0, 3).map((file, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 border border-gray-200 dark:border-zinc-700 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded ${getFileTypeColor(file.type)}`}>
                        <FileText className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white text-sm">{file.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {file.type} • {(file.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleDownloadFile(file)}
                        className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium flex items-center gap-1 transition-colors"
                      >
                        <Download className="w-3 h-3" />
                        Download
                      </button>
                      <button
                        onClick={() => handleViewFile(file)}
                        className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium flex items-center gap-1 transition-colors"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Open
                      </button>
                    </div>
                  </div>
                ))}
                {selectedBooking.files.length > 3 && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                    ...and {selectedBooking.files.length - 3} more files
                  </p>
                )}
              </div>
              <button
                onClick={() => {
                  setSelectedFiles(selectedBooking.files);
                  setSelectedBooking(null);
                  setSelectedTutorDetails(null);
                  setViewingFiles(true);
                }}
                className="mt-3 w-full px-4 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
              >
                <Files className="w-4 h-4" />
                View All Files ({selectedBooking.files.length})
              </button>
            </div>
          )}

          {/* Session Details */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-purple-500" />
              Session Details
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Sessions</p>
                <p className="font-semibold text-gray-900 dark:text-white">{selectedBooking.total_sessions}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Duration per Session</p>
                <p className="font-semibold text-gray-900 dark:text-white">{selectedBooking.session_duration} min</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Price</p>
                <p className="font-semibold text-gray-900 dark:text-white flex items-center gap-1">
                  <DollarSign className="w-4 h-4" />
                  {selectedBooking.total_price}
                </p>
              </div>
            </div>
          </div>

          {/* Goals and Notes */}
          {selectedBooking.goals && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Learning Goals</h3>
              <p className="text-gray-700 dark:text-gray-300">{selectedBooking.goals}</p>
            </div>
          )}

          {selectedBooking.additional_notes && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Additional Notes</h3>
              <p className="text-gray-700 dark:text-gray-300">{selectedBooking.additional_notes}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-6 border-t border-gray-200 dark:border-zinc-700">
            <button
              onClick={() => updateBookingStatus(selectedBooking.id, 'confirmed')}
              className="flex-1 px-4 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={selectedBooking.status === 'confirmed' || selectedBooking.status === 'completed'}
            >
              <CheckCircle className="w-4 h-4" />
              Confirm
            </button>
            <button
              onClick={() => updateBookingStatus(selectedBooking.id, 'completed')}
              className="flex-1 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={selectedBooking.status === 'completed'}
            >
              <ClipboardCheck className="w-4 h-4" />
              Mark Complete
            </button>
            <button
              onClick={() => updateBookingStatus(selectedBooking.id, 'cancelled')}
              className="flex-1 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={selectedBooking.status === 'cancelled'}
            >
              <XCircle className="w-4 h-4" />
              Cancel
            </button>
            <button
              onClick={() => deleteBooking(selectedBooking.id)}
              className="px-4 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-medium flex items-center justify-center transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  )}
</AnimatePresence>

      {/* Contact Message Detail Modal */}
      <AnimatePresence>
        {selectedMessage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedMessage(null)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-zinc-900 rounded-2xl p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Contact Message Details</h2>
                <button
                  onClick={() => setSelectedMessage(null)}
                  className="w-10 h-10 rounded-full bg-gray-200 dark:bg-zinc-800 flex items-center justify-center hover:bg-gray-300 dark:hover:bg-zinc-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="space-y-6">
                {/* Sender Information */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <User className="w-5 h-5 text-purple-500" />
                    Sender Information
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Name</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{selectedMessage.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Email</p>
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-500" />
                        <p className="font-semibold text-gray-900 dark:text-white">{selectedMessage.email}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Status</p>
                      <div className="mt-1">
                        <MessageStatusBadge status={selectedMessage.status} />
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Submitted</p>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {new Date(selectedMessage.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Message Content */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-purple-500" />
                    Message Content
                  </h3>
                  <div className="p-4 border-2 border-purple-200 dark:border-purple-800 rounded-xl bg-purple-50 dark:bg-purple-900/20">
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line">{selectedMessage.message}</p>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    {selectedMessage.message?.length || 0} characters
                  </p>
                </div>

                {/* Message Timeline */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Message Timeline</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Submitted</p>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {new Date(selectedMessage.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Last Updated</p>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {new Date(selectedMessage.updated_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-6 border-t border-gray-200 dark:border-zinc-700">
                  <button
                    onClick={() => handleReplyToMessage(selectedMessage)}
                    className="flex-1 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    Reply via Email
                  </button>
                  <button
                    onClick={() => updateMessageStatus(selectedMessage.id, 'read')}
                    className="flex-1 px-4 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={selectedMessage.status === 'read'}
                  >
                    <MailOpen className="w-4 h-4" />
                    Mark as Read
                  </button>
                  <button
                    onClick={() => updateMessageStatus(selectedMessage.id, 'replied')}
                    className="flex-1 px-4 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={selectedMessage.status === 'replied'}
                  >
                    <CheckCircle className="w-4 h-4" />
                    Mark Replied
                  </button>
                  <button
                    onClick={() => updateMessageStatus(selectedMessage.id, 'archived')}
                    className="flex-1 px-4 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={selectedMessage.status === 'archived'}
                  >
                    <XCircle className="w-4 h-4" />
                    Archive
                  </button>
                  <button
                    onClick={() => deleteMessage(selectedMessage.id)}
                    className="px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium flex items-center justify-center transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}